/**
 * BondRecordPanel — record the bonds on a case, and show what they imply (Lloyd, Aug 21 / Aug 24).
 *
 * Distinct from AppealBondPanel, which GENERATES a bond. This one records a bond that already
 * exists — bonds predating the feature live only as PDFs in mailboxes and cannot be backfilled, so
 * a case that appealed out reads as JP/municipal until someone enters its bond here.
 *
 * The panel leads with the two facts the failure-to-appear fee hangs on: the **court level** (which
 * follows a FILED appeal bond, so a signed-but-unfiled bond does not move it) and the **FTA
 * status** derived from recorded appearance outcomes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CaseBondApi, {
  BOND_TYPE_LABELS,
  BondType,
  CaseBondDto,
  CaseBondsResponse,
} from '../../api/CaseBondApi';

interface BondRecordPanelProps {
  Ticket_guid: string;
}

const emptyForm = {
  type: 'APPEAL' as BondType,
  fromCourtType: 'municipal' as 'municipal' | 'jp',
  amount: '',
  judgmentDate: '',
  signedAt: '',
  filedAt: '',
  note: '',
};

const BondRecordPanel: React.FC<BondRecordPanelProps> = ({ Ticket_guid }) => {
  const [data, setData] = useState<CaseBondsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await CaseBondApi.list(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the bonds');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const record = async () => {
    setSaving(true);
    setError(null);
    try {
      await CaseBondApi.record(Ticket_guid, {
        CaseBond_Type: form.type,
        CaseBond_FromCourtType: form.type === 'APPEAL' ? form.fromCourtType : null,
        CaseBond_AmountCents:
          form.amount.trim() === '' ? null : Math.round(Number(form.amount) * 100),
        CaseBond_JudgmentDate: form.judgmentDate || null,
        CaseBond_SignedAt: form.signedAt || null,
        CaseBond_FiledAt: form.filedAt || null,
        CaseBond_Note: form.note.trim() || null,
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the bond');
    } finally {
      setSaving(false);
    }
  };

  const setDate = async (
    bond: CaseBondDto,
    field: 'CaseBond_SignedAt' | 'CaseBond_FiledAt',
    value: string,
  ) => {
    setBusyGuid(bond.CaseBond_guid);
    setError(null);
    try {
      await CaseBondApi.updateDates(bond.CaseBond_guid, { [field]: value || null });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the bond');
    } finally {
      setBusyGuid(null);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Bond record &amp; court level
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record a bond that already exists on this case. The court level follows a <em>filed</em>{' '}
        appeal bond.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        data && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <Chip
                color={data.courtLevel.level === 'COURT_OF_RECORD' ? 'primary' : 'default'}
                label={
                  data.courtLevel.level === 'COURT_OF_RECORD'
                    ? 'Court of record ($1,500 FTA tier)'
                    : 'JP / municipal ($350 FTA tier)'
                }
              />
              <Chip
                color={data.ftaStatus.hasFailureToAppear ? 'error' : 'success'}
                variant="outlined"
                label={
                  data.ftaStatus.hasFailureToAppear
                    ? `Failure to appear recorded${
                        data.ftaStatus.failureCount > 1 ? ` ×${data.ftaStatus.failureCount}` : ''
                      }`
                    : 'No failure to appear'
                }
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {data.courtLevel.reason}
            </Typography>
            {data.courtLevel.hasUnfiledAppealBond && (
              <Alert severity="info" sx={{ mb: 2 }}>
                An appeal bond is on the case but not recorded as filed — the case has not moved
                court. Set its filing date below once it is filed.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {data.bonds.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No bonds recorded on this case.
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {data.bonds.map((b) => (
                  <Box
                    key={b.CaseBond_guid}
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {b.CaseBond_Type === 'APPEAL' ? 'Appeal bond' : 'Attorney bond'}
                      {b.CaseBond_FromCourtType ? ` — out of ${b.CaseBond_FromCourtType} court` : ''}
                      {b.CaseBond_AmountCents != null
                        ? ` — $${(b.CaseBond_AmountCents / 100).toFixed(2)}`
                        : ''}
                    </Typography>
                    {b.CaseBond_Note && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {b.CaseBond_Note}
                      </Typography>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                      <TextField
                        label="Signed"
                        type="date"
                        size="small"
                        value={b.CaseBond_SignedAt ? b.CaseBond_SignedAt.slice(0, 10) : ''}
                        onChange={(e) => setDate(b, 'CaseBond_SignedAt', e.target.value)}
                        disabled={busyGuid === b.CaseBond_guid}
                        InputLabelProps={{ shrink: true }}
                        sx={{ maxWidth: 190 }}
                      />
                      <TextField
                        label="Filed (moves the case)"
                        type="date"
                        size="small"
                        value={b.CaseBond_FiledAt ? b.CaseBond_FiledAt.slice(0, 10) : ''}
                        onChange={(e) => setDate(b, 'CaseBond_FiledAt', e.target.value)}
                        disabled={busyGuid === b.CaseBond_guid}
                        InputLabelProps={{ shrink: true }}
                        sx={{ maxWidth: 210 }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Record a bond
      </Typography>
      <Stack spacing={2} sx={{ maxWidth: 480 }}>
        <TextField
          select
          label="Bond type"
          size="small"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value as BondType })}
        >
          {(Object.keys(BOND_TYPE_LABELS) as BondType[]).map((t) => (
            <MenuItem key={t} value={t}>
              {BOND_TYPE_LABELS[t]}
            </MenuItem>
          ))}
        </TextField>
        {form.type === 'APPEAL' && (
          <TextField
            select
            label="Appealing out of"
            size="small"
            value={form.fromCourtType}
            onChange={(e) =>
              setForm({ ...form, fromCourtType: e.target.value as 'municipal' | 'jp' })
            }
          >
            <MenuItem value="municipal">Municipal court</MenuItem>
            <MenuItem value="jp">Justice court</MenuItem>
          </TextField>
        )}
        <TextField
          label="Bond amount (USD, optional)"
          type="number"
          size="small"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          inputProps={{ min: 0, step: 0.01 }}
        />
        {form.type === 'APPEAL' && (
          <TextField
            label="Judgment date (optional)"
            type="date"
            size="small"
            value={form.judgmentDate}
            onChange={(e) => setForm({ ...form, judgmentDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Signed (optional)"
            type="date"
            size="small"
            value={form.signedAt}
            onChange={(e) => setForm({ ...form, signedAt: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Filed (optional)"
            type="date"
            size="small"
            value={form.filedAt}
            onChange={(e) => setForm({ ...form, filedAt: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="This is the date that moves the case to a court of record"
          />
        </Stack>
        <TextField
          label="Note (optional)"
          size="small"
          multiline
          minRows={2}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="e.g. recorded from the signed PDF Lloyd emailed on 12 Aug"
        />
        <Box>
          <Button variant="contained" onClick={record} disabled={saving}>
            {saving ? 'Saving…' : 'Record bond'}
          </Button>
        </Box>
      </Stack>

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default BondRecordPanel;
